import React, { useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome, Psychology, SafetyCheck } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { calculateLoShuNumerology, LoShuResult } from '../utils/loShuNumerologyService';
import {
  loShuArrowName,
  loShuArrowStrength,
  loShuArrowWeakness,
  loShuDisclaimer,
  loShuNumberDirection,
  loShuNumberGuidance,
  loShuNumberMeaning,
  loShuNumberTitle,
  loShuPatternEffect,
  loShuPatternName,
  loShuText,
} from '../utils/loShuLocalization';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const numberList = (numbers: number[]) => numbers.join('-');
const formatDobInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const PremiumLockCard: React.FC<{
  title: string;
  description: string;
  cta: string;
}> = ({ title, description, cta }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.5,
      borderRadius: 2,
      bgcolor: 'rgba(255, 193, 7, 0.08)',
      borderColor: 'warning.light',
    }}
  >
    <Stack spacing={1.5}>
      <Chip label="Premium" color="warning" sx={{ alignSelf: 'flex-start', fontWeight: 600 }} />
      <Typography variant="h6" fontWeight={600}>{title}</Typography>
      <Typography color="text.secondary">{description}</Typography>
      <Button variant="contained" color="warning" onClick={() => window.location.assign('/pricing')} sx={{ alignSelf: 'flex-start' }}>
        {cta}
      </Button>
    </Stack>
  </Paper>
);

const LoShuNumerology: React.FC = () => {
  const { i18n } = useTranslation();
  const { state: authState } = useAuth();
  const isHindi = Boolean(i18n.language?.startsWith('hi'));
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const text = (en: string, hi: string) => loShuText(isHindi, en, hi);
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [result, setResult] = useState<LoShuResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCalculate = async () => {
    setError('');
    setLoading(true);

    try {
      const localResult = calculateLoShuNumerology(dob.trim(), { fullName });
      setResult(localResult);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/numerology/lo-shu`,
          { dob: dob.trim(), fullName },
          authState.token ? { headers: { Authorization: `Bearer ${authState.token}` } } : undefined
        );
        if (response.data?.data?.loShu) setResult(response.data.data.loShu);
      } catch {
        setResult(localResult);
      }
    } catch (err: any) {
      setError(err.message || text('Unable to calculate Lo Shu Grid.', 'Lo Shu Grid calculate nahi ho paya.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>{text('Lo Shu Grid Numerology Calculator | Kundlistar', 'लो शू ग्रिड न्यूमेरोलॉजी कैलकुलेटर | Kundlistar')}</title>
        <meta
          name="description"
          content={text(
            'Calculate Lo Shu Grid numerology with missing numbers, repeated numbers, arrows of strength, weakness arrows, yogas and mystical triangle insights.',
            'Missing numbers, repeated numbers, strength arrows, weakness arrows, yogs और mystical triangle insights के साथ Lo Shu Grid numerology calculate करें.'
          )}
        />
      </Helmet>

      <Stack spacing={3}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            {text('Lo Shu Grid Numerology', 'लो शू ग्रिड न्यूमेरोलॉजी')}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 820 }}>
            {text(
              'Explore the classic Lo Shu magic square using your date of birth. Kundlistar reads the fixed 4-9-2 / 3-5-7 / 8-1-6 grid, missing numbers, repeated energies, arrows, yogs and mystical triangle patterns as guidance.',
              'अपनी जन्म तारीख से classic Lo Shu magic square देखें। Kundlistar fixed 4-9-2 / 3-5-7 / 8-1-6 grid, missing numbers, repeated energies, arrows, yogs और mystical triangle patterns को guidance के रूप में पढ़ता है।'
            )}
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label={text('Full Name', 'पूरा नाम')}
                placeholder={text('Optional', 'वैकल्पिक')}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={text('Date of Birth', 'जन्म तारीख')}
                placeholder="DD/MM/YYYY"
                value={dob}
                onChange={(event) => setDob(formatDobInput(event.target.value))}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button fullWidth size="large" variant="contained" onClick={handleCalculate} disabled={loading}>
                {loading ? text('Calculating...', 'Calculate हो रहा है...') : text('Calculate Lo Shu Grid', 'Lo Shu Grid Calculate करें')}
              </Button>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Paper>

        {result && (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {fullName.trim() ? text(`${fullName.trim()}'s Lo Shu Grid`, `${fullName.trim()} का लो शू ग्रिड`) : text('Classic Lo Shu Grid', 'क्लासिक लो शू ग्रिड')}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {result.layout.flat().map((num) => (
                      <Box
                        key={num}
                        sx={{
                          minHeight: 96,
                          border: '1px solid',
                          borderColor: result.grid[num] ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 1.5,
                          bgcolor: result.grid[num] ? 'rgba(46, 125, 82, 0.08)' : 'background.default',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h5">{num}</Typography>
                          <Chip size="small" label={result.grid[num] ? `x${result.grid[num]}` : text('Missing', 'अनुपस्थित')} color={result.grid[num] ? 'primary' : 'default'} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{loShuNumberDirection(num, isHindi, result.numbers[num].direction)}</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>{result.grid[num] ? String(num).repeat(result.grid[num]) : loShuNumberTitle(num, isHindi, result.numbers[num].title)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary">{text('Driver Number', 'ड्राइवर नंबर')}</Typography>
                        <Typography variant="h4">{result.psychicNumber}</Typography>
                        <Typography variant="body2" color="text.secondary">{loShuNumberTitle(result.psychicNumber, isHindi, result.numbers[result.psychicNumber].title)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary">{text('Destiny Number', 'डेस्टिनी नंबर')}</Typography>
                        <Typography variant="h4">{result.destinyNumber}</Typography>
                        <Typography variant="body2" color="text.secondary">{loShuNumberTitle(result.destinyNumber, isHindi, result.numbers[result.destinyNumber].title)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{text('Grid Digits Used', 'Grid में इस्तेमाल अंक')}</Typography>
                        <Typography color="text.secondary">
                          {text(
                            `DOB digits: ${result.birthDigits.join(', ')}. Driver ${result.psychicNumber} and Conductor ${result.destinyNumber} are added to the Lo Shu grid; zeros are ignored.`,
                            `DOB digits: ${result.birthDigits.join(', ')}. Driver ${result.psychicNumber} और Conductor ${result.destinyNumber} को Lo Shu grid में add किया गया है; zero ignore होते हैं.`
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{text('Your Core Reading', 'आपकी Core Reading')}</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          {text('Driver', 'ड्राइवर')} {result.psychicNumber}: {loShuNumberMeaning(result.psychicNumber, isHindi, result.numbers[result.psychicNumber].meaning)}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          {text('Destiny', 'डेस्टिनी')} {result.destinyNumber}: {loShuNumberGuidance(result.destinyNumber, isHindi, result.numbers[result.destinyNumber].guidance)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{text('Present Number Strengths', 'Present Numbers की Strengths')}</Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {result.presentNumbers.map((num) => (
                            <Box key={num} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, flex: '1 1 180px' }}>
                              <Typography fontWeight={600}>{num}: {loShuNumberTitle(num, isHindi, result.numbers[num].title)}</Typography>
                              <Typography variant="body2" color="text.secondary">{loShuNumberMeaning(num, isHindi, result.numbers[num].meaning)}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{text('Repeated Numbers', 'दोहराए गए नंबर')}</Typography>
                        {result.repeatedNumbers.length ? result.repeatedNumbers.map((item) => (
                          <Typography key={item.num} color="text.secondary" sx={{ mt: 1 }}>
                            {text(item.interpretation, `${loShuNumberTitle(item.num, isHindi, item.title)} energy grid में ${item.count} बार amplify हुई है।`)}
                          </Typography>
                        )) : <Typography color="text.secondary">{text('No repeated birth digits in the Lo Shu grid.', 'Lo Shu grid में कोई repeated birth digit नहीं है।')}</Typography>}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" gutterBottom>{text('Missing Numbers and Starter Remedies', 'Missing Numbers और शुरुआती उपाय')}</Typography>
              {result.missingNumbers.length ? (
                <Grid container spacing={2}>
                  {result.missingNumbers.map((item) => (
                    <Grid size={{ xs: 12, md: 6 }} key={item.num}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography fontWeight={600}>{item.num}: {loShuNumberTitle(item.num, isHindi, item.title)}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{loShuNumberMeaning(item.num, isHindi, item.meaning)}</Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}><strong>{text('Balance tip', 'बैलेंस टिप')}:</strong> {loShuNumberGuidance(item.num, isHindi, item.lesson)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary">{text('All numbers are present. Focus on balancing repeated numbers and active arrows.', 'सभी numbers present हैं। Repeated numbers और active arrows को balance करने पर ध्यान दें।')}</Typography>
              )}
            </Paper>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <SafetyCheck color="primary" />
                    <Typography variant="h5">{text('Arrows of Strength', 'शक्ति के ऐरो')}</Typography>
                  </Stack>
                  {result.strengthArrows.length ? result.strengthArrows.map((arrow) => (
                    <Box key={arrow.id} sx={{ mb: 2 }}>
                      <Typography fontWeight={600}>{loShuArrowName(arrow, isHindi)} ({numberList(arrow.numbers)})</Typography>
                      <Typography color="text.secondary">{loShuArrowStrength(arrow, isHindi)}</Typography>
                    </Box>
                  )) : <Typography color="text.secondary">{text('No complete strength arrow formed. Partial planes still show developing areas.', 'कोई complete strength arrow नहीं बना। Partial planes developing areas दिखाते हैं।')}</Typography>}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Psychology color="primary" />
                    <Typography variant="h5">{text('Weakness Arrows', 'कमजोरी के ऐरो')}</Typography>
                  </Stack>
                  {result.weaknessArrows.length ? result.weaknessArrows.map((arrow) => (
                    <Box key={arrow.id} sx={{ mb: 2 }}>
                      <Typography fontWeight={600}>{loShuArrowName(arrow, isHindi)} ({numberList(arrow.numbers)})</Typography>
                      <Typography color="text.secondary">{loShuArrowWeakness(arrow, isHindi)}</Typography>
                    </Box>
                  )) : <Typography color="text.secondary">{text('No fully empty weakness arrow found.', 'कोई पूरी तरह empty weakness arrow नहीं मिला।')}</Typography>}
                </Paper>
              </Grid>
            </Grid>

            {isPremiumUser ? (
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <AutoAwesome color="primary" />
                  <Typography variant="h5">{text('Yogs and Mystical Triangle', 'Yogs और Mystical Triangle')}</Typography>
                </Stack>
                <Grid container spacing={2}>
                  {[...result.yogas, ...result.mysticalTriangles.filter((item) => item.active)].map((item) => (
                    <Grid size={{ xs: 12, md: 6 }} key={item.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography fontWeight={600}>{loShuPatternName(item, isHindi)} ({numberList(item.numbers)})</Typography>
                          <Typography color="text.secondary">{loShuPatternEffect(item, isHindi)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {!result.yogas.length && !result.mysticalTriangles.some((item) => item.active) && (
                    <Grid size={{ xs: 12 }}>
                      <Typography color="text.secondary">{text('No full yog or mystical triangle is active for this birth date.', 'इस जन्म तारीख के लिए कोई full yog या mystical triangle active नहीं है।')}</Typography>
                    </Grid>
                  )}
                  {result.mysticalTriangles.filter((item) => !item.active && item.presentCount > 0).map((item) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`${item.id}-partial`}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography fontWeight={600}>{text('Partial', 'आंशिक')}: {loShuPatternName(item, isHindi)} ({numberList(item.numbers)})</Typography>
                          <Typography color="text.secondary">{text(`${item.presentCount}/3 numbers present. This is a developing pattern rather than a complete triangle.`, `${item.presentCount}/3 numbers present हैं। यह complete triangle नहीं, developing pattern है।`)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ) : (
              <PremiumLockCard
                title={text('Unlock Lo Shu Yogs and Mystical Triangle', 'Lo Shu Yogs और Mystical Triangle अनलॉक करें')}
                description={text(
                  'Yog combinations, partial triangles and deeper pattern predictions are available for Kundlistar Studio Pro users.',
                  'Yog combinations, partial triangles और deeper pattern predictions Kundlistar Studio Pro users के लिए उपलब्ध हैं।'
                )}
                cta={authState.user ? text('Choose Plan', 'Premium अपग्रेड करें') : text('Login and Upgrade', 'Login करके Premium लें')}
              />
            )}

            {isPremiumUser ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h5" gutterBottom>{text('Name Correction Suggestions', 'नाम करेक्शन सुझाव')}</Typography>
                {result.nameCorrection ? (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography color="text.secondary">{text('Name Number', 'नाम नंबर')}</Typography>
                            <Typography variant="h4">{result.nameCorrection.currentNameNumber}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {loShuNumberTitle(result.nameCorrection.currentNameNumber, isHindi, result.numbers[result.nameCorrection.currentNameNumber]?.title || '')}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <Typography color="text.secondary">
                          {text(
                            `Your Lo Shu Grid has missing numbers ${result.nameCorrection.missingNumbers.join(', ') || 'none'}. To support these missing vibrations, your name spelling can be adjusted using Chaldean number letters.`,
                            `आपके Lo Shu Grid में missing numbers ${result.nameCorrection.missingNumbers.join(', ') || 'none'} हैं। इन vibrations को support करने के लिए Chaldean number letters से name spelling adjust की जा सकती है।`
                          )}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          {text(
                            'Choose a spelling that keeps the name easy to pronounce, culturally suitable and practically usable.',
                            'ऐसी spelling चुनें जो pronounce करने में आसान, culturally suitable और practical हो।'
                          )}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box>
                      <Typography fontWeight={600} gutterBottom>{text('Letters that can help', 'सहायक letters')}</Typography>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {Object.entries(result.nameCorrection.lettersByMissingNumber).map(([num, letters]) => (
                          <Chip key={num} label={`${num}: ${letters.join(', ')}`} variant="outlined" />
                        ))}
                      </Stack>
                    </Box>

                    {result.nameCorrection.bestSuggestion && (
                      <Alert severity="success">
                        <strong>{text('Best Suggestion', 'सबसे अच्छा सुझाव')}:</strong> {result.nameCorrection.summary}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      {result.nameCorrection.suggestions.map((item) => (
                        <Grid size={{ xs: 12, md: 6 }} key={`${item.suggestedName}-${item.addedLetter}-${item.supportsMissingNumber}`}>
                          <Card variant="outlined">
                            <CardContent>
                              <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
                                <Typography variant="h6" fontWeight={600}>{item.suggestedName}</Typography>
                                <Chip size="small" color={item.naturalness === 'Good' ? 'success' : 'warning'} label={text(`${item.naturalness} spelling`, `${item.naturalness} spelling`)} />
                              </Stack>
                              <Typography color="text.secondary" sx={{ mt: 1 }}>
                                {text('Name Number', 'नाम नंबर')}: <strong>{item.nameNumber}</strong> | {text('Total', 'Total')}: <strong>{item.total}</strong>
                              </Typography>
                              <Typography color="text.secondary" sx={{ mt: 1 }}>
                                {text('Added Letter', 'जोड़ा गया letter')}: <strong>{item.addedLetter}</strong> ({item.addedLetterNumber}) | {text('Supports Missing Number', 'Missing number support')}: <strong>{item.supportsMissingNumber}</strong>
                              </Typography>
                              <Typography color="text.secondary" sx={{ mt: 1 }}>{item.reason}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                ) : (
                  <Typography color="text.secondary">
                    {text('Enter full name above to view premium name-number balance guidance.', 'Premium name-number balance guidance देखने के लिए ऊपर पूरा नाम दर्ज करें।')}
                  </Typography>
                )}
              </Paper>
            ) : (
              <PremiumLockCard
                title={text('Unlock Name Balance and Correction', 'नाम बैलेंस और करेक्शन अनलॉक करें')}
                description={text(
                  'Chaldean-style name balance, name number review and correction guidance are premium-only.',
                  'Chaldean-style name balance, name number review और correction guidance केवल Pro users के लिए है।'
                )}
                cta={authState.user ? text('Choose Plan', 'Premium अपग्रेड करें') : text('Login and Upgrade', 'Login करके Premium लें')}
              />
            )}

            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" gutterBottom>{text('Balance Notes', 'बैलेंस नोट्स')}</Typography>
              {result.repeatedNumbers.length ? result.repeatedNumbers.map((item) => (
                <Box key={item.num} sx={{ mb: 1.5 }}>
                  <Typography fontWeight={600}>{text(`${item.num} repeated ${item.count} times`, `${item.num} ${item.count} बार repeat हुआ`)}</Typography>
                  <Typography color="text.secondary">{loShuNumberGuidance(item.num, isHindi, item.guidance)}</Typography>
                </Box>
              )) : <Typography color="text.secondary">{text('No repeated number pressure is visible from the date digits.', 'Date digits से repeated number pressure visible नहीं है।')}</Typography>}
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                {text(
                  'Use these insights as self-reflection: strengthen missing areas through habits, communication, learning, discipline and spiritual practice instead of expecting fixed outcomes.',
                  'इन insights को self-reflection की तरह use करें: fixed outcomes की उम्मीद करने के बजाय habits, communication, learning, discipline और spiritual practice से missing areas को मजबूत करें।'
                )}
              </Typography>
            </Paper>

            <Alert severity="info">{loShuDisclaimer(isHindi, result.disclaimer)}</Alert>
          </>
        )}
      </Stack>
    </Container>
  );
};

export default LoShuNumerology;
